import { useState, useEffect, useCallback, useMemo } from 'react';
import { Box, FormControl, Select, MenuItem, Grid, List, ListItem, ListItemText, Typography } from '@material-ui/core';
import { css } from '@emotion/react';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import { useResetRecoilState, useSetRecoilState } from 'recoil';
import { useRouter } from 'next/router';

import { useColors } from 'hooks/useColors';
import { IColors } from 'types/colors';
import { Season } from 'types/seasons';

import { i18n } from 'lib/i18n';
import { getDatesRange, getDayMonth, getDaysAndTimeForActivityTimes, getUTCDateWithFormat } from 'lib/dates';
import { BLANK_CHARACTER } from 'lib/characters';
import { isEarlyBird, isLateFeePrice } from 'lib/seasonUtils';

import { checkoutStore } from 'stores/programCheckoutStore';

import { setOpacity } from 'styles/utils';

import Button from 'components/shared/Button/Button';
import { checkoutFlow } from 'components/Checkout/checkoutFlow';

import { RegistrationTypes } from '../../Programs/registrationTypes';
import Registration from '../../Programs/Registration/Registration';
import { Product, ProductPrice } from 'types/product';
import { AlertBox } from '../../Checkout/program/step/lib/application.util';
import { EDateFormats } from 'types/times';
import { DEFAULT_TIMEZONE, isEarlier, isLater, parseWithTimezone } from '@bondsports/date-time';

const placeholderCss = css`
	height: 6rem;
`;

const boxCss = (colors: IColors) => css`
	width: 100%;
	min-height: 437px;
	display: flex;
	flex-direction: column;
	border-radius: 2px;
	background-color: ${colors.bg_white};
	border: solid 1px ${colors.bg_border_primary};
	border-top: none;
	box-shadow: 0 2px 22px 8px ${setOpacity(colors.bg_text_primary || '', 0.08)};
`;

const firstRowCss = css`
	display: flex;
	justify-content: flex-end;
`;

const timeNoteCss = (earlyBird: boolean, isLateFee: boolean, colors: IColors) => css`
	width: 126px;
	height: 31px;
	${earlyBird ? `background-color: ${colors.btnGreenBg};` : ''}
	${isLateFee ? `background-color: ${colors.bg_error};` : ''}
	border-radius: 2px;
	font-size: 1.4rem;
	font-weight: bold;
	color: ${colors.tx_white};
	margin-right: 30px;

	display: flex;
	align-items: center;
	justify-content: center;
`;

const contentWrapperCss = (emptyState?: boolean) => css`
	display: flex;
	flex-direction: column;
	${emptyState ? `height: 380px;` : ''}
	justify-content: center;
	padding: 15px 30px;
`;

const registrationTitleCss = (colors: IColors) => css`
	margin-top: 10px;

	font-size: 2.4rem;
	font-stretch: normal;
	font-style: normal;
	line-height: normal;
	letter-spacing: normal;
	color: ${colors.tx_text_primary};
`;

const seasonFormControlCss = css`
	width: 100%;

	.MuiFormLabel-root {
		text-transform: uppercase;
		padding: 8px 0;
	}
`;

const seasonSelectCss = (colors: IColors) => css`
	background-color: ${colors.bg_background_primary};
	text-indent: 12px;
	font-size: 1.6rem;
	border: none;
	height: 50px;
	&.MuiInput-root {
		margin-top: 0;
	}
	.MuiSelect-icon {
		// background-color: ${setOpacity(colors.bg_text_primary || '', 0.37)};
		background-color: ${colors.bg_text_teriaty};
		top: 0;
		width: 44px;
		height: 100%;
		color: ${colors.tx_white};
		padding: 12px;
	}
`;

const seasonBlockCss = css`
	.MuiList-padding {
		padding-bottom: 0;
	}
`;

const seasonBlockEmptyCss = css`
	height: 100%;
	display: flex;
`;

const registrationsGridEmptyCss = css`
	justify-content: center;
	align-self: center;
`;

const registrationsGridCss = (colors: IColors) => css`
	.MuiListItem-root {
		padding-left: 0;
		padding-right: 0;
	}

	li .MuiListItemText-primary {
		font-size: 1.4rem;
		font-weight: 600;
		line-height: 1.57;
		color: ${setOpacity(colors.tx_text_primary || '', 0.37)};
		letter-spacing: normal;
	}

	li .MuiTypography-colorTextSecondary {
		font-size: 1.4rem;
		font-weight: bold;
		line-height: 1;
		letter-spacing: normal;
		color: ${colors.tx_text_primary};
	}
`;

const centeredWrapperCss = css`
	display: flex;
	justify-content: center;
`;

const buttonCss = css`
	width: 100%;
	height: 50px;

	&.MuiButtonBase-root {
		margin-top: 37px;
	}
`;

const topThinRowCss = (colors: IColors) => css`
	width: 100%;
	height: 5px;
	background-image: linear-gradient(to left, ${colors.bg_system}, ${colors.bg_system} 0%);
`;

const emptyBodyCss = (colors: IColors) => css`
	font-size: 1.6rem;
	font-weight: 500;
	line-height: 1.38;
	color: ${setOpacity(colors.tx_text_primary || '', 0.31)}; ;
`;

interface SeasonProps {
	program: string;
	seasons: Season[];
	sport: number;
	isRegistrationEnabled?: boolean;
	isHideEventTimes?: boolean;
	onSeasonSelected?: (season: Season) => void;
}

function SeasonPicker(props: SeasonProps) {
	const router = useRouter();
	const { program, sport, seasons, isRegistrationEnabled = true, onSeasonSelected } = props;
	const { colors } = useColors();
	const [currentSeason, setCurrentSeason] = useState<Season | undefined>();
	const [currentPrices, setCurrentPrices] = useState<ProductPrice[] | undefined>([]);

	const currentDateInFacilityTimezone = parseWithTimezone(new Date(), currentSeason?.facilityTimezone ?? DEFAULT_TIMEZONE).utc(true);
	const isPastSeason = currentSeason?.registrationEndDate && isLater(currentDateInFacilityTimezone, currentSeason?.registrationEndDate, 'day');

	const registrationYetStarted =
		currentSeason?.registrationStartDate && isEarlier(currentDateInFacilityTimezone, currentSeason?.registrationStartDate, 'day');

	const [seasonId, setSeasonId] = useState<number>(0);

	const [dayTime, setDateTime] = useState(BLANK_CHARACTER);

	const setCheckoutState = useSetRecoilState(checkoutStore.checkout);
	const resetCheckoutState = useResetRecoilState(checkoutStore.checkout);

	const updateSeason = useCallback(
		(season: Season | undefined) => {
			if (!season) {
				return;
			}

			setCurrentSeason(season);
			setSeasonId(season.id);
			if (season.activityTimes && Array.isArray(season.activityTimes)) {
				setDateTime(getDaysAndTimeForActivityTimes(season.activityTimes).join(','));
			}

			if (season.products && (season.products as Product[]).length) {
				setCurrentPrices(
					((season.products as Product[])[0].prices ?? []).sort((p1, p2) => p1.endDate.localeCompare(p2.endDate))
				);
			}
			if (onSeasonSelected) {
				onSeasonSelected(season);
			}
		},
		[onSeasonSelected]
	);

	useEffect(() => {
		if (seasons.length !== 1) {
			return;
		}

		updateSeason(seasons[0]);
	}, [seasons, updateSeason]);

	const seasonChangeHandler = (event: React.ChangeEvent<{ value: unknown }>) => {
		const seasonId = event.target.value as number;
		setSeasonId(seasonId);
		if (seasonId > 0) {
			const season = seasons.find(seasonItem => seasonItem.id === seasonId);

			updateSeason(season);
		} else {
			setDateTime(BLANK_CHARACTER);

			setCurrentPrices([]);
		}
	};

	const bookNowHandler = () => {
		const selectedSeason = seasons.find(seasonItem => seasonItem.id === seasonId);

		if (!selectedSeason) {
			return;
		}

		// Reset the checkout state to start fresh.
		// Prevents stale state from previous sessions, leaving a cart half through.
		resetCheckoutState();

		setCheckoutState(state => {
			return {
				...state,
				currentSport: sport,
				selectedProgram: program,
				selectedSeason,
			};
		});

		router.push(`/checkout/${checkoutFlow.program[0].urlPath}`);
	};

	const invalidRegitrationDates = (isPastSeason as boolean) || (registrationYetStarted as boolean);

	const alertMessage = useMemo(() => {
		if (isPastSeason) return <AlertBox message={i18n.programs.registration.registrationOver} />;
		if (registrationYetStarted) {
		  return <AlertBox message={`${i18n.programs.registration.registrationYetStarted}${getUTCDateWithFormat(currentSeason?.registrationStartDate!, EDateFormats.YYYY_MM_DD)}`} />;
		}
		if (!isRegistrationEnabled) return <AlertBox message={`${i18n.programs.registration.windowClosed}`} />;
		return null;
	  }, [isPastSeason, registrationYetStarted, isRegistrationEnabled, currentSeason]);
	

	return (
		<Box data-aid="SeasonPicker-box" css={boxCss(colors)}>
			<div data-aid="SeasonPicker-top" css={topThinRowCss(colors)} />
			<div data-aid="SeasonPicker-first" css={firstRowCss}>
				<div
					css={timeNoteCss(
						currentSeason ? isEarlyBird(currentSeason) : false,
						currentSeason ? isLateFeePrice(currentSeason) : false,
						colors
					)}
				>
					{currentSeason && isEarlyBird(currentSeason) && <div>{i18n.programs.registration.title.earlyBird}</div>}
					{currentSeason && isLateFeePrice(currentSeason) && <div>{i18n.programs.registration.title.late}</div>}
				</div>
			</div>
			<div data-aid="SeasonPicker-content" css={contentWrapperCss(!(seasonId > 0))}>
				{alertMessage}
				<h1 data-testid="SeasonPicker-CC6871" css={registrationTitleCss(colors)}>
					{i18n.programs.season.seasonRegistrationTitle.toUpperCase()}
				</h1>
				<div css={centeredWrapperCss}>
					<FormControl css={seasonFormControlCss}>
						<Select
							data-testid="SeasonPicker-D7878E"
							labelId="programSeason"
							id="programSeason"
							value={seasonId}
							onChange={seasonChangeHandler}
							disableUnderline
							css={seasonSelectCss(colors)}
							IconComponent={ExpandMoreIcon}
						>
							{seasons.length !== 1 && <MenuItem value="0">{i18n.season.seasonPicker.selectEmpty}</MenuItem>}

							{seasons.map(season => (
								<MenuItem key={season.id} value={season.id}>
									{season.name}
								</MenuItem>
							))}
						</Select>
					</FormControl>
				</div>
				<div css={seasonId > 0 ? seasonBlockCss : seasonBlockEmptyCss}>
					{seasonId > 0 ? (
						<>
							{props.isHideEventTimes ? (
								<div css={placeholderCss} />
							) : (
								<Grid
									container
									justify="flex-start"
									spacing={2}
									css={registrationsGridCss(colors)}
									data-testid="Days&Times"
								>
									<Grid item sm={12} md={4}>
										<List>
											{currentSeason ? (
												<ListItem>
													<ListItemText
														primary={i18n.programs.season.dates}
														secondary={getDatesRange(currentSeason.startDate, currentSeason.endDate)}
													/>
												</ListItem>
											) : null}
										</List>
									</Grid>
									<Grid item sm={12} md={4}>
										<List>
											<ListItem>
												<ListItemText
													primary={i18n.programs.season.daysTime}
													secondary={
														<>
															{dayTime.split(',').map(d => {
																return <div key={d}>{d}</div>;
															})}
														</>
													}
												/>
											</ListItem>
										</List>
									</Grid>
								</Grid>
							)}
							<Grid container justify="flex-start" spacing={2} css={registrationsGridCss(colors)}>
								<Grid item sm={12} md={4}>
									{currentSeason &&
										(currentSeason.products as Product[]) &&
										(currentSeason.products as Product[])[0] &&
										currentPrices &&
										currentPrices?.[0] && (
											<Registration
												untilDate={`${i18n.programs.registration.throughDate} ${
													currentPrices?.[0]?.endDate ? getDayMonth(currentPrices[0].endDate) : ''
												}`}
												name={currentPrices?.[0]?.name}
												price={currentPrices?.[0]?.price}
												registrationType={
													/* isEarlyBird(currentSeason, registrationProducts[0].prices[0]) ? */
													currentPrices?.length > 1 ? RegistrationTypes.EARLY_BIRD : RegistrationTypes.REGULAR
												}
											/>
										)}
								</Grid>
								{/* very weird logic... we should look into it later */}
								<Grid item sm={12} md={4}>
									{currentSeason &&
										(currentSeason.products as Product[]) &&
										(currentSeason.products as Product[]) &&
										currentPrices &&
										currentPrices[1] && (
											<Registration
												untilDate={`${i18n.programs.registration.throughDate} ${getDayMonth(currentPrices[1].endDate)}`}
												name={currentPrices[1].name}
												price={currentPrices[1].price}
												registrationType={RegistrationTypes.REGULAR}
											/>
										)}
								</Grid>
								{currentSeason &&
									(currentSeason.products as Product[]) &&
									(currentSeason.products as Product[])[0] &&
									currentPrices &&
									currentPrices[2] && (
										<Grid item sm={12} md={4}>
											<Registration
												untilDate={`${i18n.programs.registration.throughDate} ${getDayMonth(currentPrices[2].endDate)}`}
												name={currentPrices[2].name}
												price={currentPrices[2].price}
												registrationType={RegistrationTypes.REGULAR}
											/>
										</Grid>
									)}
							</Grid>
						</>
					) : (
						<Grid container justify="center" css={registrationsGridEmptyCss}>
							<Grid item sm={12}>
								<Typography css={emptyBodyCss(colors)}>{i18n.season.seasonPicker.emptyBody}</Typography>
							</Grid>
						</Grid>
					)}
				</div>

				<Box css={centeredWrapperCss}>
					<Button
						data-aid="button-SeasonPicker-book"
						variant="contained"
						color="secondary"
						css={buttonCss}
						disabled={!(seasonId > 0) || invalidRegitrationDates || !isRegistrationEnabled}
						onClick={bookNowHandler}
					>
						{i18n.programs.registerNowButtonText}
					</Button>
				</Box>
			</div>
		</Box>
	);
}

export default SeasonPicker;
